<script setup lang="ts">
import { computed, ref } from "vue";
import { Tippy } from "vue-tippy";
import VIcon from "@magnit/icons/src/components/VIcon/VIcon.vue";
import CloseIcon from "@magnit/icons/src/assets/icons/close-24.svg";
import type { ITooltipProps, ITooltipEmits } from "./VTooltip.types";

const props = withDefaults(defineProps<ITooltipProps>(), {
  content: undefined,
  placement: "auto",
  theme: "light",
  appendTo: "parent",
  showArrow: true,
  hideOnClick: true,
  trigger: "mouseenter focus",
  triggerTarget: undefined,
  zIndex: 50, // --pl-z-index-tooltip
});

const emit = defineEmits<ITooltipEmits>();
defineOptions({ inheritAttrs: false });

const arrow = computed(() =>
  props.showArrow
    ? `<svg width="16" height="6" viewBox="0 0 20 8" fill="none" xmlns="http://www.w3.org/2000/svg">
<path fill-rule="evenodd" clip-rule="evenodd" d="M10 0C13 0 15.9999 8 20 8H0C3.9749 8 7 0 10 0Z"/>
</svg>`
    : false,
);

const onShow = () => {
  emit("show");
};
const onHide = () => {
  emit("hide");
};

const onClose = (hide: () => void) => {
  hide();
  emit("close");
};

const tooltip = ref<InstanceType<typeof Tippy> | null>(null);

defineExpose({
  tooltip,
});
</script>

<template>
  <Tippy
    ref="tooltip"
    class="pl-tooltip"
    :class="{ 'pl-tooltip_closable': closable }"
    :placement="props.placement"
    :theme="props.theme"
    :arrow="arrow"
    :append-to="props.appendTo"
    :trigger="props.trigger"
    :trigger-target="props.triggerTarget"
    :interactive="props.interactive"
    :z-index="props.zIndex"
    :max-width="props.maxWidth"
    :hide-on-click="props.hideOnClick"
    :show-on-create="props.showOnCreate"
    :offset="props.offset"
    :on-show="onShow"
    :on-hide="onHide"
  >
    <slot />
    <template #content="{ hide }">
      <div class="pl-tooltip__content">
        <slot name="content">
          {{ content }}
        </slot>
        <div v-if="closable" class="pl-tooltip__close" @click="onClose(hide)">
          <VIcon :color="theme === 'dark' ? 'invert-primary' : 'primary'">
            <CloseIcon />
          </VIcon>
        </div>
      </div>
    </template>
  </Tippy>
</template>

<style lang="postcss">
@import url("./styles/light.css");
@import url("./styles/main.css");
@import url("./styles/svg-arrow.css");
</style>

<style lang="postcss" scoped>
.pl-tooltip {
  display: inline-flex;

  &__content {
    display: flex;
  }

  &__close {
    display: inline-flex;
    height: fit-content;
    margin-left: 6px;
    cursor: pointer;
  }
}
</style>
